import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, Search, Printer, Edit, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

// Fields printed on each label, per mode. Keys resolve from the entry first,
// then the label header. "The fields which we select they should only display
// in the final Labels."
const LABEL_BOX_FIELDS = [
  { key: 'customer_name',      label: 'Customer Name' },
  { key: 'product_description', label: 'Description' },
  { key: 'material_code',      label: 'Material Code' },
  { key: 'po_number',          label: 'PO No' },
  { key: 'batch_number',       label: 'Batch Number' },
  { key: 'box_number',         label: 'Box Number' },
  { key: 'bundles',            label: 'No of Bundles' },
  { key: 'quantity',           label: 'Quantity' },
  { key: 'weight',             label: 'Weight' },
];
const LABEL_ROLL_FIELDS = [
  { key: 'customer_name',      label: 'Customer Name' },
  { key: 'product_description', label: 'Description' },
  { key: 'material_code',      label: 'Material Code' },
  { key: 'po_number',          label: 'PO No' },
  { key: 'batch_number',       label: 'Batch Number' },
  { key: 'roll_number',        label: 'Roll Number' },
  { key: 'gross_weight',       label: 'Gross Weight' },
  { key: 'net_weight',         label: 'Net Weight' },
];
const labelFieldsForMode = (mode) => (mode === 'roll' ? LABEL_ROLL_FIELDS : LABEL_BOX_FIELDS);

const Labels = () => {
  const [labels, setLabels] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const blankEntry = (n) => ({ box_number: String(n).padStart(2, '0'), roll_number: '', bundles: '', quantity: '', weight: '', net_weight: '', gross_weight: '' });
  const emptyForm = {
    sales_order_id: '', customer_id: '', customer_name: '', customer_address: '',
    sku: '', product_description: '', material_code: '', po_number: '',
    batch_number: '', quantity: 1, weight: '', box_count: 4, label_mode: 'box', print_fields: null,
    label_entries: [blankEntry(1), blankEntry(2), blankEntry(3), blankEntry(4)]
  };
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [labelsRes, ordersRes, custRes, invRes, settingsRes] = await Promise.all([
        api.get('/sales/labels'), api.get('/sales/orders'),
        api.get('/purchase/customers'), api.get('/sales/inventory'),
        api.get('/sales/company-settings')
      ]);
      setLabels(labelsRes.data); setOrders(ordersRes.data);
      setCustomers(custRes.data); setInventory(invRes.data);
      setCompanySettings(settingsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const formatAddress = (a) => {
    if (!a) return '';
    return [
      a.address_line1,
      a.address_line2,
      [a.city, a.state].filter(Boolean).join(', '),
      [a.pincode, a.country].filter(Boolean).join(' ')
    ].map(s => (s || '').trim()).filter(Boolean).join('\n');
  };

  const onCustomerChange = (cid) => {
    const cust = customers.find(c => c.id === cid);
    setFormData(prev => ({
      ...prev, customer_id: cid,
      customer_name: cust?.name || '',
      customer_address: cust ? formatAddress(cust.billing_address) : ''
    }));
  };

  const onProductChange = (sku) => {
    const item = inventory.find(i => i.sku === sku);
    setFormData(prev => ({
      ...prev, sku,
      product_description: item?.name || '',
      material_code: item?.sku || ''
    }));
  };

  const updateEntry = (idx, field, val) => {
    setFormData(prev => {
      const entries = [...prev.label_entries];
      entries[idx] = { ...entries[idx], [field]: val };
      return { ...prev, label_entries: entries };
    });
  };

  const updateBoxCount = (count) => {
    const n = Math.max(1, Math.min(20, parseInt(count) || 4));
    const entries = [];
    for (let i = 0; i < n; i++) {
      const existing = formData.label_entries[i] || {};
      entries.push({
        box_number: String(i + 1).padStart(2, '0'),
        roll_number: existing.roll_number || '',
        bundles: existing.bundles || '',
        quantity: existing.quantity || '',
        weight: existing.weight || '',
        net_weight: existing.net_weight || '',
        gross_weight: existing.gross_weight || ''
      });
    }
    setFormData(prev => ({ ...prev, box_count: n, label_entries: entries }));
  };

  // Which fields are printed on the label (default: all for the mode).
  const isFieldOn = (key) => {
    const pf = formData.print_fields;
    return !pf || pf.length === 0 ? true : pf.includes(key);
  };
  const toggleField = (key) => {
    setFormData(prev => {
      const all = labelFieldsForMode(prev.label_mode).map(c => c.key);
      const current = (prev.print_fields && prev.print_fields.length) ? prev.print_fields : all;
      const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
      return { ...prev, print_fields: next };
    });
  };

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm }); setShowModal(true); };

  const openEdit = (label) => {
    setEditing(label);
    const entries = (label.label_entries && label.label_entries.length > 0)
      ? label.label_entries.map(e => ({ box_number: e.box_number, roll_number: e.roll_number || '', bundles: e.bundles || '', quantity: e.quantity || '', weight: e.weight || '', net_weight: e.net_weight || '', gross_weight: e.gross_weight || '' }))
      : [blankEntry(1)];
    setFormData({
      sales_order_id: label.sales_order_id || '', customer_id: label.customer_id || '',
      customer_name: label.customer_name || '', customer_address: label.customer_address || '',
      sku: label.sku || '', product_description: label.product_description || '', material_code: label.material_code || '',
      po_number: label.po_number || '', batch_number: label.batch_number || '',
      quantity: label.quantity || 1, weight: label.weight || '',
      net_weight: label.net_weight || '', gross_weight: label.gross_weight || '',
      label_mode: label.label_mode || 'box', print_fields: label.print_fields || null,
      box_count: entries.length, label_entries: entries
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const { customer_name, customer_address, product_description, material_code, po_number, batch_number, quantity, weight, net_weight, gross_weight, box_count, label_mode, print_fields, label_entries } = formData;
        await api.put(`/sales/labels/${editing.id}`, { customer_name, customer_address, product_description, material_code, po_number, batch_number, quantity, weight, net_weight, gross_weight, box_count, label_mode, print_fields, label_entries });
      } else {
        await api.post('/sales/labels', formData);
      }
      setShowModal(false); setEditing(null); setFormData({ ...emptyForm }); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const approveLabel = async (labelId) => {
    try { await api.post(`/sales/labels/${labelId}/approve`); fetchData(); toast.success('Label approved — ready for release'); }
    catch (err) {
      const msg = err.response?.status === 403 ? 'You are not authorized to approve labels' : (err.response?.data?.detail || 'Failed');
      toast.error(msg);
    }
  };

  const markPrinted = async (labelId) => {
    try { await api.put(`/sales/labels/${labelId}/print`); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const printLabels = (label) => {
    const co = companySettings;
    const entries = label.label_entries || [];
    const totalBoxes = entries.length || label.box_count || 1;
    const mode = label.label_mode || 'box';
    const allFields = labelFieldsForMode(mode);
    const pf = label.print_fields;
    const fields = (pf && pf.length) ? allFields.filter(f => pf.includes(f.key)) : allFields;

    // Resolve a field value from the entry first, then the label header.
    const valueFor = (key, entry, idx) => {
      switch (key) {
        case 'customer_name': return label.customer_name || '';
        case 'product_description': return label.product_description || '';
        case 'material_code': return label.material_code || '';
        case 'po_number': return label.po_number || '';
        case 'batch_number': return label.batch_number || '';
        case 'box_number': return `${entry.box_number || String(idx + 1).padStart(2, '0')} of ${String(totalBoxes).padStart(2, '0')}`;
        case 'roll_number': return entry.roll_number || '';
        case 'bundles': return entry.bundles || '';
        case 'quantity': return entry.quantity || '';
        case 'weight': return entry.weight || entry.gross_weight || label.weight || '';
        case 'net_weight': return entry.net_weight || label.net_weight || '';
        case 'gross_weight': return entry.gross_weight || label.gross_weight || '';
        default: return '';
      }
    };

    const renderLabel = (entry, idx) => {
      const rows = fields.filter(f => f.key !== 'customer_name').map(f => {
        const v = valueFor(f.key, entry, idx);
        if (v === '' || v === null || v === undefined) return '';
        return `<div class="field-row"><span class="label-key">${f.label}:</span><span>${v}</span></div>`;
      }).join('');
      const showName = fields.some(f => f.key === 'customer_name');
      return `
      <div class="label">
        <div class="label-header">
          <img src="https://hridya.dweb.co.in/hridayalogo.png" alt="logo" style="height:34px; width:auto;" />
        </div>
        <div class="label-body">
          ${showName ? `<div class="field"><strong>${label.customer_name || ''}</strong></div>
          <div class="address">${(label.customer_address || '').replace(/\n/g, '<br/>')}</div>` : ''}
          ${rows}
        </div>
      </div>`;
    };

    // Group entries into pages of 4
    const pages = [];
    for (let i = 0; i < entries.length; i += 4) {
      pages.push(entries.slice(i, i + 4));
    }
    if (pages.length === 0) pages.push([{ box_number: '01', quantity: String(label.quantity), weight: label.weight || '' }]);

    const pagesHtml = pages.map((page, pi) =>
      `<div class="page ${pi > 0 ? 'page-break' : ''}">${page.map((e, i) => renderLabel(e, pi * 4 + i)).join('')}</div>`
    ).join('');

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Labels - ${label.label_number}</title>
    <style>
      @page{size:A4;margin:10mm}
      body{font-family:'Arial',sans-serif;margin:0;padding:10px}
      .page{display:grid;grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr;gap:8px;height:calc(100vh - 20px)}
      .page-break{page-break-before:always}
      .label{border:2px solid #000;padding:14px;display:flex;flex-direction:column}
      .label-header{border-bottom:2px solid #1a237e;padding-bottom:6px;margin-bottom:8px}
      .company-name{font-size:16px;font-weight:bold;color:#1a237e}
      .label-body{font-size:11px;line-height:1.6}
      .field{margin-bottom:4px;font-size:12px}
      .address{font-size:10px;margin-bottom:8px;line-height:1.3}
      .field-row{display:flex;gap:6px;margin:2px 0}
      .label-key{font-weight:bold;min-width:100px;font-size:10px}
      @media print{button{display:none !important}.page{height:auto;min-height:calc(100vh - 20mm)}}
    </style></head><body>
    ${pagesHtml}
    <button onclick="window.print()" style="margin-top:12px;padding:8px 24px;cursor:pointer">Print</button>
    </body></html>`);
    w.document.close();
  };

  const filtered = labels.filter(l =>
    (l.label_number||'').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (l.customer_name||'').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (l.batch_number||'').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (l.product_description||'').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="labels-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Labels</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="create-label-btn"><Plus size={16} /> Create Label</button>
      </div>

      <div className="card mb-4">
        <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input type="text" className="form-input" placeholder="Search label #, customer, batch, product..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
        </div>
      </div>

      <div className="card">
        {loading ? <div data-testid="loading-indicator">Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Label #</th><th>Customer</th><th>Product</th><th>Batch</th><th>Boxes</th><th>Qty</th><th>Status</th><th>Printed</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={9} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No labels</td></tr> : filtered.map(label => (
                  <tr key={label.id} data-testid={`label-row-${label.label_number}`}>
                    <td className="font-mono">{label.label_number}</td>
                    <td>{label.customer_name || '-'}</td>
                    <td>{label.product_description || label.sku}</td>
                    <td>{label.batch_number}</td>
                    <td className="tabular-nums">{label.label_entries?.length || label.box_count || 1}</td>
                    <td className="text-right tabular-nums">{label.quantity}</td>
                    <td><span className={`badge ${label.status === 'approved' ? 'badge-success' : 'badge-warning'}`}>{label.status === 'approved' ? 'Approved' : 'Draft'}</span></td>
                    <td><span className={`badge ${label.printed ? 'badge-success' : 'badge-warning'}`}>{label.printed ? 'Yes' : 'No'}</span></td>
                    <td>
                      <div className="flex gap-2">
                        {label.status !== 'approved' && !label.printed && <button onClick={() => openEdit(label)} className="btn btn-ghost btn-sm" title="Edit" data-testid={`edit-btn-${label.label_number}`}><Edit size={14} /></button>}
                        {label.status !== 'approved' && <button onClick={() => approveLabel(label.id)} className="btn btn-ghost btn-sm" title="Approve" data-testid={`approve-btn-${label.label_number}`}><CheckCircle size={14} color="#059669" /></button>}
                        <button onClick={() => printLabels(label)} className="btn btn-ghost btn-sm" title="Print" data-testid={`print-btn-${label.label_number}`}><Printer size={14} /></button>
                        {label.status === 'approved' && !label.printed && <button onClick={() => markPrinted(label.id)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px' }} data-testid={`mark-printed-${label.label_number}`}>Release</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="label-modal">
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? `Edit Label ${editing.label_number}` : 'Create Labels'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2" style={{ gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Sales Order *</label>
                    <select className="form-select" value={formData.sales_order_id} onChange={(e) => setFormData({ ...formData, sales_order_id: e.target.value })} required disabled={!!editing} data-testid="order-select">
                      <option value="">Select...</option>
                      {orders.map(o => <option key={o.id} value={o.id}>{o.order_number} - {o.customer_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Customer *</label>
                    <select className="form-select" value={formData.customer_id} onChange={(e) => onCustomerChange(e.target.value)} required disabled={!!editing} data-testid="customer-select">
                      <option value="">Select...</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Customer Address *</label>
                    <textarea className="form-input" value={formData.customer_address} onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })} required rows={3} placeholder="Full address printed on the label" style={{ resize: 'vertical' }} data-testid="customer-address" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Product *</label>
                    <select className="form-select" value={formData.sku} onChange={(e) => onProductChange(e.target.value)} required disabled={!!editing} data-testid="sku-select">
                      <option value="">Select...</option>
                      {inventory.map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <input type="text" className="form-input" value={formData.product_description} onChange={(e) => setFormData({ ...formData, product_description: e.target.value })} data-testid="description" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Material Code</label>
                    <input type="text" className="form-input" value={formData.material_code} onChange={(e) => setFormData({ ...formData, material_code: e.target.value })} data-testid="material-code" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">PO Number</label>
                    <input type="text" className="form-input" value={formData.po_number} onChange={(e) => setFormData({ ...formData, po_number: e.target.value })} data-testid="po-number" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Batch Number *</label>
                    <input type="text" className="form-input" value={formData.batch_number} onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })} required data-testid="batch-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Total Quantity *</label>
                    <input type="number" className="form-input" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })} required min={1} data-testid="quantity-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Label Type</label>
                    <select className="form-select" value={formData.label_mode} onChange={(e) => setFormData({ ...formData, label_mode: e.target.value })} data-testid="label-mode">
                      <option value="box">For Boxes</option>
                      <option value="roll">For Rolls</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Number of {formData.label_mode === 'roll' ? 'Rolls' : 'Boxes'}</label>
                    <input type="number" className="form-input" value={formData.box_count} onChange={(e) => updateBoxCount(e.target.value)} min={1} max={20} data-testid="box-count" />
                  </div>
                </div>

                <h3 style={{ fontSize: '14px', fontWeight: 700, margin: '16px 0 8px' }}>{formData.label_mode === 'roll' ? 'Roll-wise' : 'Box-wise'} Details (for each label)</h3>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  {formData.label_mode === 'roll' ? (
                    <>
                      <thead><tr>
                        <th style={{ padding: '6px', width: '80px' }}>Roll #</th>
                        <th style={{ padding: '6px' }}>Roll No.</th>
                        <th style={{ padding: '6px' }}>Net Wt (Kg)</th>
                        <th style={{ padding: '6px' }}>Gross Wt (Kg)</th>
                      </tr></thead>
                      <tbody>
                        {formData.label_entries.map((entry, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '4px', textAlign: 'center' }}>{entry.box_number} of {String(formData.box_count).padStart(2, '0')}</td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={entry.roll_number || ''} onChange={(e) => updateEntry(idx, 'roll_number', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} placeholder="Roll #" /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={entry.net_weight || ''} onChange={(e) => updateEntry(idx, 'net_weight', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} placeholder="e.g. 10.45" /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={entry.gross_weight || ''} onChange={(e) => updateEntry(idx, 'gross_weight', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} placeholder="e.g. 11.90" /></td>
                          </tr>
                        ))}
                      </tbody>
                    </>
                  ) : (
                    <>
                      <thead><tr>
                        <th style={{ padding: '6px', width: '80px' }}>Box #</th>
                        <th style={{ padding: '6px' }}>No of Bundles</th>
                        <th style={{ padding: '6px' }}>Quantity</th>
                        <th style={{ padding: '6px' }}>Weight (Kg)</th>
                      </tr></thead>
                      <tbody>
                        {formData.label_entries.map((entry, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '4px', textAlign: 'center' }}>{entry.box_number} of {String(formData.box_count).padStart(2, '0')}</td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={entry.bundles} onChange={(e) => updateEntry(idx, 'bundles', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} placeholder="e.g. 8 x 200" /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={entry.quantity} onChange={(e) => updateEntry(idx, 'quantity', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} placeholder="e.g. 1,600 nos" /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={entry.weight || ''} onChange={(e) => updateEntry(idx, 'weight', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} placeholder="e.g. 20.20" /></td>
                          </tr>
                        ))}
                      </tbody>
                    </>
                  )}
                </table>

                <div style={{ marginTop: 14, padding: '10px 12px', background: 'var(--bg-surface-alt, #f8fafc)', borderRadius: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Fields to show on the final label</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    {labelFieldsForMode(formData.label_mode).map(c => (
                      <label key={c.key} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="checkbox" checked={isFieldOn(c.key)} onChange={() => toggleField(c.key)} data-testid={`lf-${c.key}`} /> {c.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-label-btn">{editing ? 'Update Label' : 'Create Labels'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Labels;
